/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';

import $ from 'jquery';
import { map } from 'lodash';

import { isSupportedAudioExtension } from 'bundles/cml/legacy/utils/AssetUtils';

const loadVideojsForAudio = () => import('bundles/phoenix/utils/loadVideojsForAudio');

type AudioNodeProps = {
  src: string;
  extension: string;
  name: string;
  ariaLabel?: string;
};

const styles = {
  audioPlayer: css`
    .vjs-coursera-phoenix-audio-skin {
      .vjs-volume-panel {
        &:focus-within {
          flex: 0 0 108px;
          background-color: #e5e7e8;

          .vjs-volume-control {
            width: 56px;
          }
        }
      }
    }
  `,
};

const AudioNode = ({ src, extension, name, ariaLabel }: AudioNodeProps) => {
  return (
    // disable a11y lint recommendation for <track> as we don't have that feature available for audio yet.
    // eslint-disable-next-line
    <audio controls={true} className="vjs-coursera-phoenix-audio-skin" preload="metadata">
      <source src={src} type={`audio/${extension}`} />
      {/* fallback to link for any unsupported cases */}
      <a href={src} className="cml-asset-link" target="_blank" aria-label={ariaLabel} rel="noopener noreferrer">
        {name}
      </a>
    </audio>
  );
};

type Props = {
  children: JSX.Element;
};

/**
 * Wrapper that replaces all child audio asset nodes with an interactive audio player via videoJS
 * */
class CMLAudioJS extends React.Component<Props> {
  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;

    loadVideojsForAudio().then((videojsModule) => {
      const videojs = videojsModule.default;
      if (this._isMounted) {
        // eslint-disable-next-line react/no-find-dom-node
        const dom = ReactDOM.findDOMNode(this);

        if (!dom) {
          return;
        }

        const audio = $(dom).find('.cml-asset-audio');

        map(audio, (assetEl: HTMLElement) => {
          const src = assetEl.getAttribute('data-url');
          const extension = assetEl.getAttribute('data-extension');
          const name = assetEl.getAttribute('data-name') as string;

          if (src && extension && isSupportedAudioExtension(extension)) {
            // retain ariaLabel so it can be added back after the DOM is replaced
            const ariaLabel = $(assetEl.childNodes[0]).attr('aria-label');
            const audioNode = $(
              ReactDOMServer.renderToStaticMarkup(
                <AudioNode src={src} name={name} extension={extension} ariaLabel={ariaLabel} />
              )
            ).get(0);

            const audioControlChildren = {
              width: '100%',
              height: '54px',
              children: [
                'mediaLoader',
                'errorDisplay',
                {
                  name: 'controlBar',
                  children: [
                    {
                      name: 'cPlayToggle',
                      isAudio: true,
                    },
                    'TimeDisplay',
                    'ProgressControlCustom',
                    'VolumePanelCustom',
                  ],
                },
              ],
            };

            // eslint-disable-next-line
            assetEl.innerHTML = '';

            assetEl.appendChild(audioNode);

            if (ariaLabel) {
              // set the previously retained ariaLabel
              $(assetEl.childNodes[0]).attr('aria-label', ariaLabel);
            }

            videojs(audioNode, audioControlChildren);
          }
        });
      }
    });
  }

  shouldComponentUpdate() {
    return false;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    return <div css={styles.audioPlayer}>{this.props.children}</div>;
  }
}

export default CMLAudioJS;
